import React, { useState, useEffect } from 'react';
import { Database, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { toast } from 'sonner';

const SupabaseSettingsDialog = ({ isOpen, onClose, project, supabase }) => {
    const [supabaseUrl, setSupabaseUrl] = useState('');
    const [supabaseAnonKey, setSupabaseAnonKey] = useState('');
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        if (isOpen && project) {
            // Load saved settings
            const settings = project.supabase_settings || {};
            setSupabaseUrl(settings.url || '');
            setSupabaseAnonKey(settings.anon_key || '');
            if (settings.tables) {
                setTables(settings.tables);
            }
        }
    }, [isOpen, project]);

    const testConnection = async () => {
        if (!supabaseUrl || !supabaseAnonKey) {
            toast.error('Please enter both URL and Anon Key');
            return;
        }

        setTesting(true);
        try {
            // Fetch table schemas
            const response = await fetch(`${supabaseUrl}/rest/v1/`, {
                headers: {
                    'apikey': supabaseAnonKey,
                    'Authorization': `Bearer ${supabaseAnonKey}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to connect to Supabase');
            }

            // Get tables from OpenAPI spec
            const specResponse = await fetch(`${supabaseUrl}/rest/`, {
                headers: {
                    'apikey': supabaseAnonKey,
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                    'Accept': 'application/openapi+json'
                }
            });

            if (specResponse.ok) {
                const spec = await specResponse.json();
                const paths = spec.paths || {};
                const tableList = [];

                for (const path in paths) {
                    if (path.startsWith('/')) {
                        const tableName = path.slice(1);
                        if (tableName && !tableName.includes('{')) {
                            // Get schema for this table
                            const schema = spec.definitions?.[tableName];
                            const columns = schema?.properties ? Object.keys(schema.properties) : [];
                            tableList.push({ name: tableName, columns });
                        }
                    }
                }

                setTables(tableList);
                toast.success(`Connected! Found ${tableList.length} tables`);
            } else {
                toast.success('Connected to Supabase!');
            }
        } catch (error) {
            console.error('Connection error:', error);
            toast.error('Failed to connect. Check your credentials.');
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const { error } = await supabase
                .from('projects')
                .update({
                    supabase_settings: {
                        url: supabaseUrl,
                        anon_key: supabaseAnonKey,
                        tables: tables
                    }
                })
                .eq('id', project.id);

            if (error) throw error;

            toast.success('Supabase settings saved!');
            onClose();
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        Supabase Integration
                    </DialogTitle>
                    <DialogDescription>
                        Connect your Supabase project to automatically insert collected data
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="supabase-url">Supabase Project URL</Label>
                        <Input
                            id="supabase-url"
                            placeholder="https://your-project.supabase.co"
                            value={supabaseUrl}
                            onChange={(e) => setSupabaseUrl(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="supabase-key">Supabase Anon Key</Label>
                        <Input
                            id="supabase-key"
                            type="password"
                            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                            value={supabaseAnonKey}
                            onChange={(e) => setSupabaseAnonKey(e.target.value)}
                        />
                    </div>

                    <Button
                        onClick={testConnection}
                        disabled={testing || !supabaseUrl || !supabaseAnonKey}
                        variant="outline"
                        className="w-full"
                    >
                        {testing ? 'Testing...' : 'Test Connection & Fetch Tables'}
                    </Button>

                    {tables.length > 0 && (
                        <div className="mt-4 p-4 bg-muted rounded-lg">
                            <h4 className="text-sm font-semibold mb-2">Available Tables ({tables.length})</h4>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                {tables.map(table => (
                                    <div key={table.name} className="text-xs font-mono bg-background px-2 py-1 rounded">
                                        {table.name} ({table.columns?.length || 0} columns)
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={loading || !supabaseUrl || !supabaseAnonKey}>
                        {loading ? 'Saving...' : 'Save Settings'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SupabaseSettingsDialog;
